// Configuration
import * as ConfigConst from "./configuration/service/ConfigService";
import ConfigService from "./configuration/service/ConfigService";
import ConfigWebService from "./configuration/service/ConfigWebService";
import ConfigConverter from "./configuration/converter/ConfigConverter";
import ConfigParser from "./configuration/parser/ConfigParser";
import ConfigPersist from "./configuration/persistence/ConfigPersist";
import UIDGeneratorPersist from "./UIDGenerator/persistence/UIDGeneratorPersist";
import UIDGenerator from "./UIDGenerator/UIDGenerator";
import Security from "./utils/Security";
import Logger from "./utils/Logger";
import ScriptService from "./script/ScriptService";
import GpsService from "./gps/service/GpsService";
import DeviceService from "./device/service/DeviceService";
import CustomFieldsManager from "./customfields/manager/CustomFieldsManager";
import EQError from './utils/EQError';
import GatewayService from './gateway/service/GatewayService';

/*eslint-disable no-undef*/
var es6polyfill = require('es6-promise');
es6polyfill.polyfill();

export const SDK_MODE_STANDARD = 0;
export const SDK_MODE_CHROME_EXTENSION = 1;

/**
 * EQualOne SDK Core
 */
export default class Core {

    constructor(url, folder) {
        Logger.log('Core init');

        // Check if run in a background container
        // eslint-disable-line
        if (window && window.chrome && chrome.runtime && chrome.runtime.id  && chrome.extension.getBackgroundPage && chrome.extension.getBackgroundPage() === window) {
            this.sdkMode = SDK_MODE_CHROME_EXTENSION;
        } else {
            this.sdkMode = SDK_MODE_STANDARD;
        }

        // UID Generator
        this.uidGenerator = new UIDGenerator(
            new UIDGeneratorPersist(),
            new Security()
        );

        // Custom fields manager
        this.customFieldsManager = new CustomFieldsManager();

        // Create configuration service
        this.configService = new ConfigService(
            new ConfigWebService(url, folder),
            new ConfigConverter(),
            new ConfigParser(),
            new ConfigPersist()
        );

        // Create gateway service
        this.gatewayService = new GatewayService();

        // Reload service with loaded configuration
        this.reloadServices(this.configService.currentConfig);
    }

    /**
     * Init the SDK
     *
     * @param configurationCallback A callback for update configuration
     * @param gatewayCallback A callback for gateway / router detection
     * @param gatewayIp The gateway IP address (if null, auto detect)
     * @param gatewayUsername The gateway username (if null, default username)
     * @param gatewayPassword The gateway password (if null, default password)
     * @param discoverGateway Au to discover gateway. Default is false.
     */
    init(configurationCallback, gatewayCallback = null, gatewayIp = null, gatewayUsername = null, gatewayPassword = null, discoverGateway = false) {
        this.configService.init((status, error) => {
            this.onConfigurationUpdate(status, error, configurationCallback);
        }, null);

        if (discoverGateway) {
            this.gatewayService.init(gatewayCallback, gatewayIp, gatewayUsername, gatewayPassword);
        }

        Logger.log("SDK Mode : " + ((this.sdkMode === SDK_MODE_CHROME_EXTENSION) ? "Chrome extension (background)":"Browser"));
    }

    /**
     * Set the router gateway informations
     *
     * @param callback
     * @param ip
     * @param username
     * @param password
     */
    setGatewayAccessInformations(callback = null, ip = null, username = null, password = null) {
        this.gatewayService.init(callback, ip, username, password);
    }


    /**
     * This method proxied the update config callback to reload services if necessary
     * Once services are restarted, the method will alert client by calling callback with status and error
     *
     * @param status
     * @param error
     * @param callback
     */
    onConfigurationUpdate(status, error, callback) {
        if (status == ConfigConst.CONFIG_SUCCESS) {
            this.reloadServices(this.configService.currentConfig);
        }
        callback(status, error);
    }

    /**
     * Update the configuration without checking last configuration update date (nextCheckDate)
     *
     * @param callback A callback with 2 parameters (status, error)
     * @param coupon Optional parameter
     */
    updateConfig(callback, coupon = null) {
        this.configService.update((status, error) => {
            this.onConfigurationUpdate(status, error, callback);
        }, coupon);
    }

    /**
     * This method will reload all services if a configuration is set as parameter
     *
     * @param configuration
     */
    reloadServices(configuration) {
        if (configuration) {
            this.gpsService = new GpsService(configuration.gpsConfig);
            this.gpsService.start();
            this.deviceService = new DeviceService();
            this.scriptService = new ScriptService(this.uidGenerator.getUID(), this.configService.currentConfig, this.gpsService, this.deviceService, this.gatewayService);
        }
    }

    /**
     * Set a reverse geocoding function to enrich GPS data from client side
     * The callback should get 3 parameters
     * The 3rd parameter (callback) should have 1 parameter : the reverse geocoding data, under the following format :
     * {city: "City", country: "Country", countryCode: "CO", postalCode: "12345", streetNumber: "1", streetName: "Quality street"}
     * WARNING : This method should be called only once you have configuration retrieved
     *
     * @param reverseGeocodingFunction A function with 3 parameters (latitude, longitude, another callback to alert SDK when enrichment is successfully done)
     */
    setReverseGeocodingFunction(reverseGeocodingFunction) {
        if (this.gpsService) {
            this.gpsService.setReverseGeocodingFunction(reverseGeocodingFunction);
            this.gpsService.restart();
        } else {
            throw new EQError('Reverse geocoding function must be set when configuration has been retrieved', 6872);
        }
    }

    /**
     * Add a custom field
     *
     * @param key
     * @param value
     */
    addCustomField(key, value) {
        this.customFieldsManager.add(key, value);
    }

    /**
     * Delete a custom field
     *
     * @param key
     */
    deleteCustomField(key) {
        this.customFieldsManager.delete(key);
    }

    /**
     * Called when popup chrome agent disconnect
     */
    disconnectFromBackgroundPage() {
        if (this.sdkMode === SDK_MODE_CHROME_EXTENSION) {
            Logger.log("Front page disconnect");
            this.scriptService.stopAll();
        }
    }
}


// WEBPACK FOOTER //
// ./js/core/Core.js